<template>
  <div class="w-full overflow-visible sm:rounded-lg catalog-list">
    <!-- Show skeleton when loading -->
    <div v-if="loading" class="flex justify-center items-center h-full">
      <hs-table-skeleton :columns="skeletonColumns" :rows="localCatalogPerPage + 1" />
    </div>

    <hs-table v-else-if="headers && headerKeyMapping" :headers="headers" :data="catalog"
      :header-key-mapping="headerKeyMapping" @row-click="handleRowClick">
      <!-- Select column header -->
      <template #header-select>
        <div class="flex items-center">
          <input id="checkbox-all" type="checkbox" :checked="isAllSelected" @change="handleSelectAll" @click.stop
            class="catalog-table-checkbox" />
          <label for="checkbox-all" class="sr-only">checkbox</label>
        </div>
      </template>

      <!-- Select column cell -->
      <template #cell-select="{ row }">
        <div class="flex items-center">
          <input :id="'checkbox-table-' + row.id" type="checkbox"
            :checked="selectedCatalog.some((item) => item.id === row.id)" :disabled="!hasSku(row)"
            @change="handleSelectItem(row, $event.target.checked)" @click.stop class="catalog-table-checkbox" />
          <label :for="'checkbox-table-' + row.id" class="sr-only">checkbox</label>
        </div>
      </template>

      <!-- Name column cell -->
      <template #cell-name="{ row }">
        <div class="catalog-cell catalog-cell--name" :title="row.name">
          {{ row.name || "-" }}
        </div>
      </template>

      <!-- SKU column cell -->
      <template #cell-item_sku="{ row }">
        <div class="catalog-cell catalog-cell--sku" :title="row.sku || '-'">
          {{ row.sku || "-" }}
        </div>
      </template>

      <!-- Barcode column cell -->
      <template #cell-barcode="{ row }">
        <div class="catalog-cell catalog-cell--barcode" :title="row.barcode || '-'">
          {{ row.barcode || "-" }}
        </div>
      </template>

      <!-- Quantity column cell -->
      <template #cell-quantity="{ row }">
        <div v-if="activeTab === 'inventory' && showInventoryCommitments" class="catalog-inventory-balance" :title="inventoryBalanceTitle(row)">
          <div class="catalog-inventory-balance__primary" :class="inventoryStateClass(row)">
            {{ formatBalanceQuantity(row.availableQuantity ?? row.quantityRaw) }} {{ row.default_quantity_unit || row.quantity_unit || '' }}
          </div>
          <div class="catalog-inventory-balance__chips">
            <span :class="balanceChipClass('onHand', row.onHandQuantity)" :aria-label="`On hand ${formatBalanceQuantity(row.onHandQuantity)}`">
              On hand {{ formatBalanceQuantity(row.onHandQuantity) }}
            </span>
            <span :class="balanceChipClass('reserved', row.reservedQuantity)" :aria-label="`Reserved ${formatBalanceQuantity(row.reservedQuantity)}`">
              Held {{ formatBalanceQuantity(row.reservedQuantity) }}
            </span>
            <span :class="balanceChipClass('available', row.availableQuantity)" :aria-label="`Available ${formatBalanceQuantity(row.availableQuantity)}`">
              Avail {{ formatBalanceQuantity(row.availableQuantity) }}
            </span>
            <span :class="balanceChipClass('deficit', row.deficitQuantity)" :aria-label="`Deficit ${formatBalanceQuantity(row.deficitQuantity)}`">
              Def {{ formatBalanceQuantity(row.deficitQuantity) }}
            </span>
          </div>
        </div>
        <div v-else class="catalog-cell catalog-cell--quantity" :title="displayQuantity(row.quantityRaw ?? row.quantity)">
          {{ displayQuantity(row.quantityRaw ?? row.quantity) }}
        </div>
      </template>

      <!-- Price column cell -->
      <template #cell-price="{ row }">
        <div class="catalog-cell catalog-cell--price" :class="getPriceColorClass(row)">
          <div class="flex items-center">
            <span class="font-medium">{{
              row.priceFormatted || row.price || "-"
            }}</span>
            <price-analytics-tooltip v-if="activeTab === 'inventory' && row.id && row.price" :product-id="row.id"
              :product-name="row.name" :store-id="storeId" :current-price="row.price?.unit_price || 0"
              :analytics="priceAnalytics[row.id]" :loading="loadingPriceAnalytics"
              :is-open="openPriceAnalyticsProductId === row.id" @price-updated="handlePriceUpdated"
              @toggle="$emit('toggle-price-analytics', row.id)" />
            <button v-if="activeTab === 'services' && row.id" @click.stop="
              $emit('open-price-modal', {
                type: 'service',
                itemId: row.id,
                itemName: row.name,
                currentPrice: Number(row.priceRaw?.unit_price || 0),
                currency: row.priceRaw?.currency || 'RWF',
                item: row,
              })
              " class="catalog-table-icon-action" type="button">
              <vue-feather type="edit-3" size="14" class="text-blue-600" />
            </button>
            <button v-if="canEditProductSalePrice(row)" @click.stop="
              $emit('open-price-modal', {
                type: 'product',
                itemId: row.id,
                itemName: row.name,
                currentPrice: Number((row.priceRaw || row.price)?.unit_price ?? 0),
                currency: (row.priceRaw || row.price)?.currency || 'RWF',
                item: row,
              })
              " class="catalog-table-icon-action" type="button">
              <vue-feather type="edit-3" size="14" class="text-blue-600" />
            </button>
          </div>
        </div>
      </template>

      <!-- Expiration Date column cell -->
      <template #cell-expiration_date="{ row }">
        <div class="catalog-cell catalog-cell--date" :title="formatExpirationDate(row.expiration_date)">
          {{ formatExpirationDate(row.expiration_date) }}
        </div>
      </template>

      <!-- Purchases: Expiration cell with severity badge -->
      <template v-if="activeTab === 'purchases'" #cell-expiration="{ row }">
        <td class="whitespace-nowrap text-sm">
          <span :class="[
            'catalog-table-badge cursor-pointer',
            expirationSeverity(row.expiration) === 'expired' &&
            'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
            expirationSeverity(row.expiration) === 'urgent' &&
            'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
            expirationSeverity(row.expiration) === 'warning' &&
            'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
            expirationSeverity(row.expiration) === 'ok' &&
            'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
            expirationSeverity(row.expiration) === 'none' &&
            'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
          ]" :title="formatExpirationDate(row.expiration)">
            {{ formatRelative(row.expiration) }}
          </span>
        </td>
      </template>

      <!-- Purchases: Unit Price cell (no analytics on stock tab) -->
      <template v-if="activeTab === 'purchases'" #cell-unit_price="{ row }">
        <td class="whitespace-nowrap text-sm text-gray-900 dark:text-white pl-0">
          {{ formatPrice(row.unit_price) }}
        </td>
      </template>

      <!-- Purchases: Added (created_at) cell -->
      <!-- Note: hs-table slots key off the header label, not the data key -->
      <template v-if="activeTab === 'purchases'" #cell-added="{ row }">
        <td class="whitespace-nowrap text-sm text-gray-900 dark:text-white pl-0"
          :title="formatDateTime(row.created_at)">
          {{ formatDateTime(row.created_at) }}
        </td>
      </template>

      <!-- Common status badge for transfers/requests/removals -->
      <template #cell-status="{ row }">
        <td class="whitespace-nowrap text-sm">
          <span :class="[
            'catalog-table-badge',
            (row.status || '').toUpperCase() === 'COMPLETED' &&
            'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
            (row.status || '').toUpperCase() === 'IN_FULFILLMENT' &&
            'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
            (row.status || '').toUpperCase() === 'APPROVED' &&
            'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
            (row.status || '').toUpperCase() === 'PENDING' &&
            'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
            (row.status || '').toUpperCase() === 'REJECTED' &&
            'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
          ]">
            {{ (row.status || "").toString() }}
          </span>
        </td>
      </template>

      <!-- Time datetime cell (for transfers/requests) -->
      <template #cell-time="{ row }">
        <td class="whitespace-nowrap text-sm text-gray-900 dark:text-white pl-0">
          {{ formatDateTime(row.created_at) }}
        </td>
      </template>

      <!-- Actions for transfers and transfer_requests -->
      <template v-if="['transfers', 'transfer_requests'].includes(activeTab)" #cell-actions="{ row }">
        <td class="whitespace-nowrap text-sm">
          <button v-if="activeTab === 'transfers'" class="catalog-table-primary-btn"
            @click.stop="$emit('view-transfer', row)" title="View transfer details">
            <hs-icon name="eye" size="14" class="mr-1.5" />
            View
          </button>
          <button v-else-if="activeTab === 'transfer_requests'" class="catalog-table-primary-btn"
            @click.stop="$emit('view-request', row)" title="View and manage request">
            <hs-icon name="eye" size="14" class="mr-1.5" />
            View
          </button>
        </td>
      </template>

      <!-- Action column cell -->
      <template v-if="['products', 'services', 'purchases'].includes(activeTab)" #cell-action="{ row }">
        <div class="catalog-table-actions">
          <button @click.stop="$emit('view-item-history', row)" class="catalog-table-link catalog-table-link--purple">
            {{ activeTab === "services" ? "Sales" : "Sales & Purchases" }}
          </button>
          <template v-if="['products', 'services'].includes(activeTab)">
            <button @click.stop="$emit('edit-item', row)" class="catalog-table-icon-btn catalog-table-icon-btn--blue"
              :title="`Edit ${row.name || 'item'}`" :aria-label="`Edit ${row.name || 'item'}`">
              <vue-feather type="edit-2" size="14" />
            </button>
            <button @click.stop="$emit('delete-item', row)" class="catalog-table-icon-btn catalog-table-icon-btn--red"
              :title="`Delete ${row.name || 'item'}`" :aria-label="`Delete ${row.name || 'item'}`">
              <vue-feather type="trash-2" size="14" />
            </button>
          </template>
        </div>
      </template>

      <!-- Save to EBM column cell -->
      <template #cell-ebm_actions="{ row }">
        <div v-if="activeTab === 'inventory'" class="flex items-center justify-center gap-2">
          <!-- Edit EBM Data Button (for products with existing EBM metadata) -->
          <button v-if="row.has_rra_ebm_metadata && ebmActivated" @click.stop="handleEditEbmDataClick(row)"
            class="catalog-table-ebm-btn catalog-table-ebm-btn--blue" title="Edit EBM data for this item">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z">
              </path>
            </svg>
            Edit EBM Data
          </button>

          <!-- Save to EBM Button (for products without EBM metadata) -->
          <button v-if="!row.has_rra_ebm_metadata && ebmActivated" @click.stop="handleEditEbmDataClick(row)"
            class="catalog-table-ebm-btn catalog-table-ebm-btn--green" title="Save this item to EBM">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"></path>
            </svg>
            Save to EBM
          </button>
          <div v-else-if="saveToEbmState(row) === 'not_activated'" class="catalog-table-ebm-state"
            title="EBM is not activated for this store">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z">
              </path>
            </svg>
            EBM Not Activated
          </div>
        </div>
      </template>
    </hs-table>

    <!-- Fallback when headers are not ready -->
    <div v-else class="flex justify-center items-center h-32">
      <div class="text-gray-500">Loading table headers...</div>
    </div>

    <!-- pagination -->
    <hs-pagination v-if="!loading" :current-page="currentPage" :total-pages="totalPages" :total-items="totalCatalog"
      :items-per-page="localCatalogPerPage" @previous-page="$emit('previous-page')" @next-page="$emit('next-page')"
      @update:items-per-page="handlePerPageChange" />
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import PriceAnalyticsTooltip from "./PriceAnalyticsTooltip.vue";
import { HsIcon } from "@/components/ui";

export default {
  name: "CatalogTable",
  components: {
    PriceAnalyticsTooltip,
    HsIcon,
  },
  props: {
    catalog: { type: Array, required: true },
    selectedCatalog: { type: Array, default: () => [] },
    currentPage: { type: Number, required: true },
    totalPages: { type: Number, required: true },
    totalCatalog: { type: Number, required: true },
    catalogPerPage: { type: Number, required: true, default: 10 },
    loading: { type: Boolean, required: true },
    activeTab: { type: String, required: true },
    showInventoryCommitments: { type: Boolean, default: false },
    loadingEbmActivation: { type: Boolean, default: false },
    storeId: { type: Number, required: false },
    priceAnalytics: { type: Object, default: () => ({}) },
    loadingPriceAnalytics: { type: Boolean, default: false },
    openPriceAnalyticsProductId: { type: Number, default: null },
  },
  data() {
    return {
      localCatalogPerPage: this.catalogPerPage,
      perPageOptions: [10, 25, 50, 100],
    };
  },
  computed: {
    ...mapGetters(["rraEbmSettings"]),
    ebmActivated() {
      return Boolean(this.rraEbmSettings?.ebm_activated);
    },

    // total number of rows that can be selected (have a SKU)
    totalSelectable() {
      return this.catalog?.filter(this.hasSku).length || 0;
    },

    // true iff every selectable row is in selectedCatalog
    isAllSelected() {
      const idsSelected = new Set(this.selectedCatalog.map((i) => i.id));
      const selectableIds = this.catalog.filter(this.hasSku).map((i) => i.id);
      return (
        selectableIds.length > 0 &&
        selectableIds.every((id) => idsSelected.has(id))
      );
    },

    headers() {
      // Keep columns stable per tab (no conditional addition that can desync cells)
      const base = {
        products: [
          "Select",
          "Name",
          "ITEM SKU",
          "Barcode",
          "Quantity Unit",
          // "Price",
          "Action",
        ],
        services: [
          "Select",
          "Name",
          "Description",
          "ITEM SKU",
          "Price",
          "Action",
        ],
        inventory: [
          "Select",
          "Name",
          "ITEM SKU",
          "Barcode",
          "Quantity",
          "Price",
          "EBM Actions",
        ],
        purchases: [
          "Name",
          "ITEM SKU",
          "Barcode",
          "Quantity",
          "Unit Price",
          "Expiration",
          "Added",
          "Action",
        ],
        transfers: ["No.", "From", "To", "Items", "Status", "Time", "Actions"],
        transfer_requests: [
          "No.",
          "From",
          "To",
          "Items",
          "Status",
          "Time",
          "Actions",
        ],
        removals: ["Product", "Quantity", "Reason", "Time", "Status"],
      };
      // Ensure we always return an array
      const result = base[this.activeTab] || base.inventory;
      return Array.isArray(result) ? result : base.inventory;
    },

    headerKeyMapping() {
      const map = {
        products: {
          Select: "select",
          Name: "name",
          "ITEM SKU": "sku",
          Barcode: "barcode",
          "Quantity Unit": "quantity_unit",
          // Price: "price",
          Action: "action",
        },
        services: {
          Select: "select",
          Name: "name",
          Description: "description",
          "ITEM SKU": "sku",
          Price: "price",
          Action: "action",
        },
        inventory: {
          Select: "select",
          Name: "name",
          "ITEM SKU": "sku",
          Barcode: "barcode",
          Quantity: "quantity",
          Price: "price",
          "EBM Actions": "save_to_ebm",
        },
        purchases: {
          Name: "name",
          "ITEM SKU": "sku",
          Barcode: "barcode",
          Quantity: "quantity",
          "Unit Price": "unit_price",
          Expiration: "expiration",
          Added: "created_at",
          Action: "action",
        },
        transfers: {
          "No.": "id",
          From: "from",
          To: "to",
          Items: "items_count",
          Status: "status",
          Time: "created_at",
          Actions: "actions",
        },
        transfer_requests: {
          "No.": "id",
          From: "from",
          To: "to",
          Items: "items_count",
          Status: "status",
          Time: "created_at",
          Actions: "actions",
        },
        removals: {
          Product: "product_name",
          Quantity: "quantity",
          Reason: "reason",
          Time: "created_at",
          Status: "status",
        },
      };
      // Ensure we always return an object
      const result = map[this.activeTab] || map.inventory;
      return typeof result === "object" && result !== null
        ? result
        : map.inventory;
    },

    skeletonColumns() {
      // Matches the column counts in headers()
      const base = {
        products: 7,
        services: 6,
        inventory: 8,
        purchases: 8,
        transfers: 7,
        transfer_requests: 7,
        removals: 5,
      };
      return base[this.activeTab] || base.inventory; // Fallback to inventory if activeTab is invalid
    },
  },
  methods: {
    // --- helpers
    toBool(v) {
      // robust boolean coercion for values like "false", 0, null
      if (typeof v === "string") return v.toLowerCase() === "true";
      return Boolean(v);
    },
    formatPrice(price) {
      if (!price) return "RWF 0";
      return `RWF ${parseFloat(price).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}`;
    },
    handlePriceUpdated({ productId, newPrice }) {
      this.$emit("price-updated", { productId, newPrice });
    },
    canEditProductSalePrice(row) {
      if (!row?.id) return false;
      if (this.activeTab === "products") return true;
      if (this.activeTab !== "inventory") return false;

      const price = row.priceRaw || row.price || {};
      return Number(price?.unit_price ?? 0) === 0;
    },
    getPriceColorClass(row) {
      // Only apply color coding on inventory tab
      if (this.activeTab !== "inventory" || !row.id || !row.price) {
        return "text-gray-900 dark:text-white";
      }

      const analytics = this.priceAnalytics[row.id];
      if (!analytics) {
        return "text-gray-900 dark:text-white";
      }

      const currentPrice = parseFloat(row.price?.unit_price || 0);
      const minPrice = analytics.min_purchase_unit_price;
      const maxPrice = analytics.max_purchase_unit_price;

      // Critical: Price below minimum cost (selling at a loss)
      if (currentPrice < minPrice) {
        return "text-red-600 dark:text-red-400 font-semibold";
      }

      // Warning: Price below maximum cost (low margin)
      if (currentPrice < maxPrice) {
        return "text-yellow-600 dark:text-yellow-400 font-semibold";
      }

      // Within 10% of max cost (close call - make it more alerting)
      const margin = (currentPrice - maxPrice) / maxPrice;
      if (margin < 0.1) {
        return "text-orange-600 dark:text-orange-400 font-semibold";
      }

      // Good: Healthy margin
      return "text-green-600 dark:text-green-400 font-semibold";
    },
    hasSku(row) {
      return typeof row?.sku === "string" && row.sku.trim().length > 0;
    },
    hasEbmMeta(row) {
      return this.toBool(row?.has_rra_ebm_metadata);
    },
    formatExpirationDate(date) {
      // Use global formatDate utility for consistency
      return this.$formatDate(date);
    },
    formatDateTime(date) {
      // Use global formatDateTime utility for consistency
      return this.$formatDateTime(date);
    },
    displayQuantity(q) {
      return q === 0 ? 0 : (q ?? "-");
    },
    formatBalanceQuantity(value) {
      const numeric = Number(value ?? 0);
      if (!Number.isFinite(numeric)) return "0";
      return numeric.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    inventoryBalanceTitle(row) {
      const unit = row.default_quantity_unit || row.quantity_unit || "";
      return [
        `On hand ${this.formatBalanceQuantity(row.onHandQuantity)} ${unit}`,
        `Reserved ${this.formatBalanceQuantity(row.reservedQuantity)} ${unit}`,
        `Available ${this.formatBalanceQuantity(row.availableQuantity)} ${unit}`,
        `Deficit ${this.formatBalanceQuantity(row.deficitQuantity)} ${unit}`,
      ].join(" · ");
    },
    inventoryStateClass(row) {
      if (Number(row.deficitQuantity || 0) > 0) return "catalog-inventory-balance__primary--red";
      if (Number(row.availableQuantity || 0) <= 0) return "catalog-inventory-balance__primary--amber";
      return "catalog-inventory-balance__primary--green";
    },
    balanceChipClass(kind, value) {
      const numeric = Number(value || 0);
      return [
        "catalog-inventory-chip",
        kind === "deficit" && numeric > 0
          ? "catalog-inventory-chip--red"
          : kind === "reserved" && numeric > 0
            ? "catalog-inventory-chip--blue"
            : kind === "available" && numeric <= 0
              ? "catalog-inventory-chip--red"
              : kind === "available"
                ? "catalog-inventory-chip--green"
                : "catalog-inventory-chip--neutral",
      ];
    },
    // Relative expiration helpers for purchases
    daysUntil(date) {
      if (!date) return null;
      const now = new Date();
      const d = new Date(date);
      return Math.round((d - now) / (1000 * 60 * 60 * 24));
    },
    formatRelative(date) {
      if (!date) return "-";
      const now = new Date();
      const target = new Date(date);
      const diff = target.getTime() - now.getTime();
      const past = diff < 0;
      const absDays = Math.round(Math.abs(diff) / (1000 * 60 * 60 * 24));

      // Today
      if (absDays === 0) {
        return past ? "Expired today" : "Expires today";
      }

      if (absDays < 7) {
        const unit = absDays === 1 ? "day" : "days";
        return past
          ? `Expired ${absDays} ${unit} ago`
          : `Expires in ${absDays} ${unit}`;
      }
      const weeks = Math.round(absDays / 7);
      if (weeks < 8) {
        const unit = weeks === 1 ? "week" : "weeks";
        return past
          ? `Expired ${weeks} ${unit} ago`
          : `Expires in ${weeks} ${unit}`;
      }
      const months = Math.round(absDays / 30);
      if (months < 24) {
        const unit = months === 1 ? "month" : "months";
        return past
          ? `Expired ${months} ${unit} ago`
          : `Expires in ${months} ${unit}`;
      }
      const years = Math.round(absDays / 365);
      const unit = years === 1 ? "year" : "years";
      return past
        ? `Expired ${years} ${unit} ago`
        : `Expires in ${years} ${unit}`;
    },
    expirationSeverity(date) {
      if (!date) return "none";
      const days = this.daysUntil(date);
      if (days === null) return "none";
      if (days < 0) return "expired";
      if (days <= 7) return "urgent";
      if (days <= 30) return "warning";
      return "ok";
    },
    saveToEbmState(row) {
      // Centralized, single source of truth for cell rendering
      if (this.activeTab !== "inventory") return "not_inventory";
      if (!this.ebmActivated) return "not_activated";
      if (this.hasEbmMeta(row)) return "ready";
      return "ok";
    },

    // --- events
    handleSelectAll(event) {
      const checked = event.target.checked;
      this.$emit("select-all", checked);
    },
    handleSaveToEbmClick(row) {
      console.log("CatalogTable: Save to EBM clicked for row:", row);
      console.log("CatalogTable: Emitting save-to-ebm event");
      this.$emit("save-to-ebm", row);
    },
    handleEditEbmDataClick(row) {
      console.log("CatalogTable: Edit EBM data clicked for row:", row);
      console.log("CatalogTable: Emitting edit-ebm-data event");
      this.$emit("edit-ebm-data", row);
    },
    handleSelectItem(item, isSelected) {
      this.$emit("select-item", item, isSelected);
    },
    handlePerPageChange(newValue) {
      this.localCatalogPerPage = newValue;
      this.$emit("update:catalogPerPage", newValue);
    },
    handleRowClick({ row, rowIndex }) {
      console.log("CatalogTable: Row clicked in CatalogTable:", {
        row: row,
        rowIndex: rowIndex,
        activeTab: this.activeTab,
        timestamp: new Date().toISOString(),
        rowId: row.id,
        rowName: row.name,
        rowSku: row.sku,
        hasEbmMetadata: row.has_rra_ebm_metadata,
      });
      // Emit the row click event to parent component
      this.$emit("row-click", { row, rowIndex, activeTab: this.activeTab });
    },

    // --- debug
    logCatalogItems() {
      console.log("=== CATALOG ITEMS DEBUG ===");
      console.log(
        "EBM Activated:",
        this.ebmActivated,
        "Loading:",
        this.loadingEbmActivation,
      );
      console.log(
        "Active Tab:",
        this.activeTab,
        "Total Items:",
        this.catalog.length,
      );

      this.catalog.forEach((item, index) => {
        const state = this.saveToEbmState(item);
        console.log(`Item ${index + 1}:`, {
          id: item.id,
          name: item.name,
          sku: item.sku,
          has_rra_ebm_metadata: item.has_rra_ebm_metadata,
          has_rra_ebm_metadata_type: typeof item.has_rra_ebm_metadata,
          has_rra_ebm_metadata_boolean: this.hasEbmMeta(item),
          ebmActivated: this.ebmActivated,
          ebmActivated_type: typeof this.ebmActivated,
          saveToEbmState: state,
        });
      });
      console.log("=== END CATALOG ITEMS DEBUG ===");
    },
  },
  mounted() {
    console.log(
      "EBM Activated:",
      this.ebmActivated,
      "Loading EBM Activation:",
      this.loadingEbmActivation,
    );
    this.logCatalogItems();
  },
  watch: {
    catalogPerPage(newValue) {
      this.localCatalogPerPage = newValue;
    },
    ebmActivated(newValue) {
      console.log("EBM Activated changed to:", newValue);
    },
    loadingEbmActivation(newValue) {
      console.log("Loading EBM Activation changed to:", newValue);
    },
    catalog: {
      handler() {
        console.log("Catalog data changed, logging items:");
        this.logCatalogItems();
      },
      immediate: true,
    },
  },
};
</script>

<style>
/* Hide scrollbar for Chrome, Safari and Opera */
.catalog-list ::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.catalog-list {
  -ms-overflow-style: none;
  /* IE and Edge */
  scrollbar-width: none;
  /* Firefox */
}

.catalog-list {
  display: grid;
  gap: 14px;
}

.catalog-list :deep(.hs-table) {
  table-layout: fixed;
}

.catalog-list :deep(.hs-table-head-sticky),
.catalog-list :deep(.hs-table-cell-sticky) {
  min-width: 42px;
  width: 42px;
}

.catalog-list :deep(.hs-table-action-head),
.catalog-list :deep(.hs-table-action-cell) {
  min-width: 240px;
  width: 240px;
}

.catalog-cell {
  color: var(--hs-text, #0f172a);
  display: block;
  font-size: 0.82rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.catalog-cell--name {
  font-weight: 800;
}

.catalog-cell--sku,
.catalog-cell--barcode {
  max-width: 120px;
}

.catalog-cell--quantity,
.catalog-cell--price,
.catalog-cell--date {
  max-width: 140px;
}

.catalog-inventory-balance {
  display: grid;
  gap: 5px;
  min-width: 190px;
}

.catalog-inventory-balance__primary {
  font-size: 0.84rem;
  font-weight: 900;
  line-height: 1.15;
  white-space: nowrap;
}

.catalog-inventory-balance__primary--green {
  color: #059669;
}

.catalog-inventory-balance__primary--amber {
  color: #d97706;
}

.catalog-inventory-balance__primary--red {
  color: #dc2626;
}

.catalog-inventory-balance__chips {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.catalog-inventory-chip {
  align-items: center;
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.62rem;
  font-weight: 900;
  line-height: 1;
  min-height: 20px;
  padding: 0 7px;
  white-space: nowrap;
}

.catalog-inventory-chip--neutral {
  background: color-mix(in srgb, var(--hs-muted, #64748b) 11%, transparent);
  color: var(--hs-muted, #64748b);
}

.catalog-inventory-chip--blue {
  background: rgba(59, 130, 246, 0.12);
  color: #2563eb;
}

.catalog-inventory-chip--green {
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
}

.catalog-inventory-chip--red {
  background: rgba(239, 68, 68, 0.12);
  color: #dc2626;
}

.catalog-table-checkbox {
  appearance: none;
  background: var(--hs-surface, #ffffff);
  border: 1.5px solid color-mix(in srgb, var(--hs-muted, #64748b) 32%, transparent);
  border-radius: 6px;
  cursor: pointer;
  height: 1.05rem;
  position: relative;
  transition: background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
  width: 1.05rem;
}

.catalog-table-checkbox:checked {
  background: linear-gradient(135deg, var(--hs-primary, #3293ec), #2563eb);
  border-color: transparent;
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--hs-primary, #3293ec) 14%, transparent);
}

.catalog-table-checkbox:checked::after {
  border: solid white;
  border-width: 0 2px 2px 0;
  content: "";
  height: 8px;
  left: 5px;
  position: absolute;
  top: 2px;
  transform: rotate(45deg);
  width: 4px;
}

.catalog-table-checkbox:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.catalog-table-badge {
  align-items: center;
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.01em;
  min-height: 25px;
  padding: 0 9px;
}

.catalog-table-actions {
  align-items: center;
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  justify-content: flex-start;
  min-width: max-content;
}

.catalog-table-link,
.catalog-table-primary-btn,
.catalog-table-ebm-btn,
.catalog-table-ebm-state,
.catalog-table-icon-action {
  align-items: center;
  border-radius: 999px;
  display: inline-flex;
  font-weight: 900;
  transition: background 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.catalog-table-link {
  font-size: 0.78rem;
  min-height: 30px;
  padding: 0 10px;
  white-space: nowrap;
}

.catalog-table-icon-btn {
  align-items: center;
  border-radius: 999px;
  display: inline-flex;
  height: 30px;
  justify-content: center;
  transition: background 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
  width: 30px;
}

.catalog-table-icon-btn:hover {
  transform: translateY(-1px);
}

.catalog-table-icon-btn--blue {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 11%, transparent);
  color: var(--hs-primary, #3293ec);
}

.catalog-table-icon-btn--red {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

.catalog-table-link:hover,
.catalog-table-primary-btn:hover,
.catalog-table-ebm-btn:hover,
.catalog-table-icon-action:hover {
  transform: translateY(-1px);
}

.catalog-table-link--purple {
  background: rgba(147, 51, 234, 0.1);
  color: #7e22ce;
}

.catalog-table-link--blue {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 11%, transparent);
  color: var(--hs-primary, #3293ec);
}

.catalog-table-link--red {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

.catalog-table-primary-btn,
.catalog-table-ebm-btn {
  color: white;
  font-size: 0.78rem;
  gap: 7px;
  min-height: 34px;
  padding: 0 12px;
}

.catalog-table-primary-btn,
.catalog-table-ebm-btn--blue {
  background: linear-gradient(135deg, var(--hs-primary, #3293ec), #2563eb);
  box-shadow: 0 10px 22px color-mix(in srgb, var(--hs-primary, #3293ec) 22%, transparent);
}

.catalog-table-ebm-btn--green {
  background: linear-gradient(135deg, #10b981, #059669);
  box-shadow: 0 10px 22px rgba(16, 185, 129, 0.2);
}

.catalog-table-ebm-state {
  background: color-mix(in srgb, var(--hs-muted, #64748b) 10%, transparent);
  color: var(--hs-muted, #64748b);
  font-size: 0.78rem;
  gap: 7px;
  min-height: 34px;
  padding: 0 12px;
}

.catalog-table-icon-action {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 10%, transparent);
  color: var(--hs-primary, #3293ec);
  height: 28px;
  justify-content: center;
  margin-left: 8px;
  width: 28px;
}
</style>
